import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { UserButton } from "@clerk/clerk-react";
import api from "../api";

const navItems = [
  {
    path: "/",
    label: "Tableau de bord",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z" />
      </svg>
    ),
  },
  {
    path: "/segments",
    label: "Segments",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    path: "/pricing",
    label: "Tarifs",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    path: "/guide",
    label: "Guide",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
  },
];

export default function Layout({ children }) {
  const location = useLocation();
  const [user, setUser] = useState(null);
  // null = statut pas encore connu (évite flash « non connecté » → « connecté »)
  const [linkedInConnected, setLinkedInConnected] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const refreshLinkedInStatus = useCallback(async () => {
    try {
      const data = await api.get("/api/linkedin/status");
      // Strict: only true when API explicitly says connected
      setLinkedInConnected(data?.connected === true);
    } catch {
      setLinkedInConnected(false);
    }
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) {
      try {
        setUser(JSON.parse(stored));
      } catch {
        /* ignore */
      }
    }

    const refreshMe = () => {
      api
        .get("/api/auth/me")
        .then((data) => {
          setUser(data);
          localStorage.setItem("user", JSON.stringify(data));
        })
        .catch(() => {});
    };

    refreshMe();
    // Keep freemium/premium badge aligned with Clerk (backend syncs every 5s too)
    const timer = setInterval(refreshMe, 5000);
    return () => clearInterval(timer);
  }, []);

  // Refresh LinkedIn status on mount, navigation, focus, and connect/disconnect events
  useEffect(() => {
    refreshLinkedInStatus();

    const onStatusEvent = (e) => {
      setLinkedInConnected(e.detail?.connected === true);
    };
    const onFocus = () => refreshLinkedInStatus();

    window.addEventListener("linkedin-status", onStatusEvent);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("linkedin-status", onStatusEvent);
      window.removeEventListener("focus", onFocus);
    };
  }, [location.pathname, refreshLinkedInStatus]);

  return (
    <div className="min-h-screen flex bg-cream font-sans">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-ink/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-60 bg-paper/92 backdrop-blur-xl border-r border-line flex flex-col transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Brand + plan status */}
        <div className="border-b border-line">
          <div className="h-16 flex items-center px-6">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-8 h-8 bg-rouge rounded-lg flex items-center justify-center shadow-rouge">
                <span className="text-white font-bold text-sm font-display">V</span>
              </div>
              <span className="text-lg font-display font-semibold text-ink tracking-tight">
                MmeValentine
              </span>
            </Link>
          </div>
          <div className="px-6 pb-3 space-y-2">
            {user ? (
              (() => {
                const plan = (user.plan || "freemium").toLowerCase();
                const isPremium = plan === "premium";
                return (
                  <>
                    <span
                      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wide uppercase px-2.5 py-1 rounded-md ${
                        isPremium
                          ? "bg-ink text-cream"
                          : "bg-ink/5 text-ink/55 border border-line"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          isPremium ? "bg-amber-300" : "bg-ink/30"
                        }`}
                      />
                      {isPremium ? "Premium" : "Freemium"}
                    </span>
                    {isPremium ? (
                      <Link
                        to="/pricing"
                        className="block text-[11px] font-medium text-ink/55 hover:text-ink hover:underline"
                      >
                        Gérer l’abonnement
                      </Link>
                    ) : (
                      <Link
                        to="/pricing"
                        className="block text-[11px] font-medium text-rouge hover:underline"
                      >
                        Passer Premium — 15 $/mois
                      </Link>
                    )}
                  </>
                );
              })()
            ) : (
              <span className="inline-block h-6 w-20 rounded-md bg-ink/5 animate-pulse" />
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => {
            const isActive =
              item.path === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-rouge/5 text-rouge"
                    : "text-ink/60 hover:text-ink hover:bg-ink/5"
                }`}
              >
                {item.icon}
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* LinkedIn Status */}
        <div className="px-4 py-4 border-t border-line space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <span
              className={`w-2.5 h-2.5 rounded-full ${
                linkedInConnected === null
                  ? "bg-ink/20 animate-pulse"
                  : linkedInConnected
                    ? "bg-emerald-500"
                    : "bg-rouge"
              }`}
            />
            <span className="text-ink/60">
              LinkedIn{" "}
              {linkedInConnected === null ? (
                <span className="text-mute">…</span>
              ) : linkedInConnected ? (
                <span className="text-emerald-600">connecté</span>
              ) : (
                <span className="text-rouge">non connecté</span>
              )}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-mute">
            <Link to="/cgu" className="hover:text-ink">
              CGU
            </Link>
            <Link to="/confidentialite" className="hover:text-ink">
              Confidentialité
            </Link>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-paper/60 backdrop-blur-md border-b border-line flex items-center justify-between px-4 lg:px-8">
          {/* Mobile menu button */}
          <button
            className="lg:hidden text-ink/40 hover:text-ink"
            onClick={() => setSidebarOpen(true)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <div className="hidden lg:block" />

          {/* User menu */}
          <div className="flex items-center gap-4">
            {user && (
              <span className="text-sm text-ink/70">
                {user.name || user.email}
              </span>
            )}
            <UserButton afterSignOutUrl="/login">
              <UserButton.MenuItems>
                <UserButton.Link
                  label="Tarifs & abonnement"
                  href="/pricing"
                  labelIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      className="w-4 h-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  }
                />
              </UserButton.MenuItems>
            </UserButton>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
